import React, { useCallback, useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from "react-router-dom";
import { timeDay } from 'd3-time';

import PresentationContext from './PresentationContext';
import { getView } from './Routes';
import { setViewDates } from './actions/time';
import { urlDateFormat } from './utils/formats';

function reduceDays(days) {
  return days.reduce((o, d) => {
    o[d] = true;
    return o;
  }, {});
}

const defaultState = {
  activeDays: {},
  stateDay: urlDateFormat(new Date()),
  stateDays: [],
  invalidRange: false,
  setActiveDays: f => f,
  setStateDay: f => f,
  setStateDays: f => f,
  toggleDay: f => f,
};

const DayFilterContext = React.createContext(defaultState);

export function DayFilterContextProvider({ children }) {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const propsDays = params.days;
  const reactDispatch = useDispatch();
  const { user } = useSelector(state => state.session, shallowEqual);
  const [invalidRange, setInvalidRange] = useState(false);
  // The active day
  const [stateDay, setStateDay] = useState(defaultState.stateDay);
  // All days presented in days view
  const [stateDays, setStateDays] = useState([]);
  // Filtered days from days view
  const [activeDays, setActiveDays] = useState(reduceDays(stateDays));

  const { parseDate, weekInterval } = useContext(PresentationContext);

  useEffect(() => {
    reactDispatch(setViewDates({
      modifier: 'selectByDay',
      selectedDates: stateDays,
      selectBy: 'days',
      user,
    }));
  }, [reactDispatch, stateDays, user]);

  const { pathname, search } = location;

  const setNewDays = useCallback((newDays, updateUrl = true) => {
    setStateDays(newDays);
    setActiveDays(reduceDays(newDays));
    if (updateUrl) {
      const currentView = getView(pathname);
      const newPath = `../${currentView}/${newDays[0]}${currentView !== 'day'
      ? `_${newDays[newDays.length - 1]}`
      : ''
      }`
      history.replace({
        pathname: newPath,
        search,
      });
    }
  }, [history, pathname, search, setActiveDays, setStateDays]);

  useEffect(() => {
    const daysMatch = (propsDays && propsDays.match) ? propsDays.match(/(\d{4}-\d{2}-\d{2})/g) : null;
    if (daysMatch && daysMatch.length === 1) {
      const propsDay = daysMatch[0];
      if (!stateDays.length || (stateDays.indexOf(propsDay) < 0)) {
        const d = parseDate(propsDay);
        if (d === null) {
          console.error('Unable to parse date', propsDay);
          return;
        }
        const startOfWeek = weekInterval.offset(weekInterval(d), ((stateDays.length && (d <= parseDate(stateDays[0])))) ? -1 : 0);
        const newDays = timeDay.range(startOfWeek, weekInterval.offset(startOfWeek, 2)).map(urlDateFormat);
        setNewDays(newDays, false);
      }
      setStateDay(propsDay);
    } else if (propsDays) {
      let newDays;
      if ((propsDays.indexOf('_') > 0) && daysMatch && daysMatch.length === 2) {
        newDays = timeDay.range(
          parseDate(daysMatch[0]),
          timeDay.offset(parseDate(daysMatch[1]), 1),
        ).map(urlDateFormat);
      } else {
        newDays = daysMatch || [];
      }
      if (stateDays.length && (stateDays.indexOf(newDays[0]) === 0)) {
        // console.error('NOT changing', { stateDays, newDays });
        // No need to change
        return;
      }
      if (!newDays.length) {
        // invalid date range
        setInvalidRange(true);
      } else {
        /*
        if (newDays.length > 31) {
          // Do we need this check?
          console.log('Limiting day count', JSON.stringify(newDays), newDays);
          newDays.splice(31, newDays.length - 31);
          setInvalidRange(true);
        } else {
          setInvalidRange(false);
        }
        */
        setNewDays(newDays);
      }
      if (!stateDay || newDays.indexOf(stateDay) <= 0) {
        setStateDay(newDays[0]);
      }
    }
  }, [parseDate, propsDays, setNewDays, stateDay, stateDays, weekInterval]);

  function selectDay(day) {
    setStateDay(day);
    const push = getView((history || document).location.pathname) !== 'day';
    history[push ? 'push' : 'replace'](`../day/${day}`);
  }

  function checkAndSetActiveDays(newActiveDays) {
    let checkedActiveDays = { ...newActiveDays };
    if (!Object.values(newActiveDays).some(v => v)) {
      // All days have been disabled, rather enable them all!
      checkedActiveDays = reduceDays(stateDays);
    }
    setActiveDays(checkedActiveDays);
    if (stateDay && !checkedActiveDays[stateDay]) {
      // we need to move the selected day forward or backward
      const view = getView((history || document).location.pathname);
      const setDay = view === 'day' ? selectDay : setStateDay;
      const index = stateDays.indexOf(stateDay);
      for (let i = 1; i < stateDays.length - 1; i += 1) {
        const nextDay = stateDays[index + i];
        if (checkedActiveDays[nextDay]) {
          setDay(nextDay);
          break;
        }
        if (i <= index) {
          const previousDay = stateDays[index - i];
          if (checkedActiveDays[previousDay]) {
            setDay(previousDay);
            break;
          }
        }
      }
    }
  }

  function toggleDay(day) {
    const newActiveDays = { ...activeDays, [day]: !activeDays[day] };
    checkAndSetActiveDays(newActiveDays);
  }

  let parsedDay;
  let parsedDays;
  if (propsDays) {
    const matchedDays = propsDays.match(/(\d{4}-\d{2}-\d{2})/g) || [];
    if (!stateDay) {
      parsedDay = matchedDays[0];
    }
    if (!stateDays && matchedDays.length) {
      parsedDays = timeDay.range(parseDate(matchedDays[0]),
        timeDay.offset(parseDate(matchedDays[matchedDays.length - 1]), 1))
      .map(urlDateFormat);
    }
  }
  if (!stateDay && !stateDays && propsDays) {
  }

  if (!activeDays) {
    setActiveDays(reduceDays(stateDays));
  } else if (Object.keys(activeDays).some(d => stateDays.indexOf(d) < 0)) {
    // console.error('mismatch with days', { activeDays: Object.keys(activeDays), stateDays });
    setActiveDays(reduceDays(stateDays));
  }

  const state = {
    ...defaultState,
    activeDays,
    day: stateDay || parsedDay,
    days: stateDays || parsedDays,
    invalidRange,
    setActiveDays: checkAndSetActiveDays,
    setDay: selectDay,
    setDays: setNewDays,
    toggleDay,
  };

  return (
    <DayFilterContext.Provider value={state}>
      {children}
    </DayFilterContext.Provider>
  );
}

export default DayFilterContext;
